// =============================================================================
// File: frontend/src/components/forms/DigitalTwinPage.jsx
// Digital Twin Dashboard — 4 tabs: Overview, Simulations, Patterns, Schedule
// Patent claims exercised: P-011, P-012, P-013 (all frontend-facing claims)
// =============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE } from '../../config';
import TwinOverview from '../TwinOverview';
import TwinSimulations from '../TwinSimulations';
import TwinPatterns from '../TwinPatterns';
import TwinSchedule from '../TwinSchedule';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'simulations', label: 'Simulations' },
  { key: 'patterns', label: 'Patterns' },
  { key: 'schedule', label: 'Schedule' },
];

export default function DigitalTwinPage({ fetchWithAuth, setActiveTab: setParentTab }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [twinState, setTwinState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const hasSynced = useRef(false);

  const loadTwinState = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchWithAuth(`${API_BASE}/api/twin/state`);
      if (res.ok) {
        const data = await res.json();
        setTwinState(data);
      } else {
        setError('Failed to load digital twin state');
      }
    } catch (err) {
      setError('Network error loading twin state');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  const syncTwin = useCallback(async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      const res = await fetchWithAuth(`${API_BASE}/api/twin/sync`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setSyncResult(data);
        // Reload twin state after sync completes
        await loadTwinState();
      } else {
        setSyncResult({ error: 'Sync failed' });
      }
    } catch (err) {
      setSyncResult({ error: 'Network error during sync' });
    } finally {
      setSyncing(false);
    }
  }, [fetchWithAuth, loadTwinState]);

  // Auto-sync on page load (once)
  useEffect(() => {
    if (!hasSynced.current) {
      hasSynced.current = true;
      syncTwin();
    }
  }, [syncTwin]);

  return (
    <div className="page-layout">
      <div className="page-sticky-nav">
        <div className="page-header-row">
          <h2 className="page-section-title">Digital Twin</h2>
          <button className="page-exit-btn" onClick={() => setParentTab('dashboard')}>&larr; Dashboard</button>
        </div>

        <div className="page-nav-bar">
          {/* ===== PILL TOOLBAR — Quick Action Tools ===== */}
          <div className="pill-toolbar">
            <button
              className="pill-btn"
              onClick={() => setActiveTab('simulations')}
              title="Run a what-if simulation"
            >
              Run Simulation
            </button>
            <button
              className="pill-btn"
              onClick={() => setActiveTab('patterns')}
              title="Discover health patterns"
            >
              Discover Patterns
            </button>
            <button
              className="pill-btn"
              onClick={() => setActiveTab('schedule')}
              title="Optimize medication schedule"
            >
              Optimize Schedule
            </button>
          </div>

          {/* 4-Tab Navigation */}
          <div className="tab-bar">
            {TABS.map(tab => (
              <button
                key={tab.key}
                className={`tab-item${activeTab === tab.key ? ' tab-item--active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content — scrollable area */}
      <div className="page-scroll-content dt-content">
        {error && (
          <div className="dt-error">
            <p>{error}</p>
            <button className="btn btn-primary" onClick={loadTwinState}>Retry</button>
          </div>
        )}

        {activeTab === 'overview' && (
          <TwinOverview
            twinState={twinState}
            loading={loading}
            syncing={syncing}
            syncResult={syncResult}
            fetchWithAuth={fetchWithAuth}
            onRefresh={loadTwinState}
            onSync={syncTwin}
          />
        )}

        {activeTab === 'simulations' && (
          <TwinSimulations
            fetchWithAuth={fetchWithAuth}
            twinState={twinState}
          />
        )}

        {activeTab === 'patterns' && (
          <TwinPatterns
            fetchWithAuth={fetchWithAuth}
          />
        )}

        {activeTab === 'schedule' && (
          <TwinSchedule
            fetchWithAuth={fetchWithAuth}
          />
        )}
      </div>
    </div>
  );
}
